import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

import { Icons } from "../../../../assets/icons";
import { ReactComponent as BackIcon } from "../../../../assets/icons/back.svg";
import {
  ZERO,
  messages,
  ALL_EVENT,
  EMPTY_STRING,
  ADD_RECAPZ_STEPS,
  eventBookingStatus,
} from "../../../../constants";
import {
  customToast,
  isArrayWithItems,
  isAddRecapzIdentical,
  getReOrderedRecapList,
  generateUniqueIdForReels,
  transformReelsInEditEvent,
} from "../../../../shared";
import CustomTooltip from "../../../../shared/components/ToolTip/CustomTooltip";
import ConfirmationModal from "../../../../shared/components/Modal/ConfirmationModal";
import MultiSelectDropdown from "../../../../shared/components/Fields/MultiSelectDropdown";
import InputGroupWrapper from "../../../../shared/components/InputGroup/InputGroupWrapper";
import { eventBookingActions } from "../../../../redux/actions";

const { ChevronDownIcon } = Icons;

// Returns { deliveredCount, hasDelivered } for a given eventId from editEvents.reels
const getDeliveredReelInfo = (eventId, editEvents) => {
  const reels = editEvents?.reels ?? [];
  const isCombined = eventId === ALL_EVENT.id;

  const matching = reels.filter((r) =>
    isCombined
      ? r.isCombinedEventsReel
      : !r.isCombinedEventsReel &&
        r.eventOrderItemMap?.some((m) => m.eventId === eventId),
  );

  const deliveredCount = matching.filter((r) => r.isReelDelivered).length;
  return { deliveredCount, hasDelivered: deliveredCount > 0 };
};

const SelectEventQuantity = (props) => {
  const { t } = useTranslation("common");

  const {
    handleActiveStep,
    formState,
    addRecapz,
    setAddRecapz,
    isAddUpdateRecap,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [recapBriefConfirmModalMessage, setRecapBriefConfirmModalMessage] =
    useState(EMPTY_STRING);

  const handleQuantityChange = (eventId, value) => {
    let quantity = parseInt(value, 10);

    const updated = [...addRecapz];
    const index = updated.findIndex((x) => x.eventId === eventId);

    const defaultRecapType = props.recapTypes[0]?.name || EMPTY_STRING;
    const defaultRecapPrice = props.recapTypes[0]?.price || ZERO;

    if (index !== -1) {
      const existingRecap = Array.isArray(updated[index].reelDetail)
        ? [...updated[index].reelDetail]
        : [];

      // get unique id for reels
      const uniqueId = generateUniqueIdForReels(props.editEvents, addRecapz);

      if (Number.isInteger(quantity) && quantity > 0) {
        if (quantity > existingRecap.length) {
          // Add new reel
          const additional = Array.from(
            { length: quantity - existingRecap.length },
            (_, i) => ({
              id: uniqueId + i,
              status: eventBookingStatus.CREATED,
              reelMode: defaultRecapType,
              price: defaultRecapPrice,
              reelName: `${updated[index].eventName} - Recap ${
                existingRecap.length + i + 1
              }`,
              eventOrderItemMap:
                eventId !== ALL_EVENT.id
                  ? [
                      {
                        eventId,
                        orderItemId: uniqueId + i,
                        isActive: true,
                      },
                    ]
                  : isArrayWithItems(selectedEventIds)
                    ? selectedEventIds.map((id) => ({
                        eventId: id,
                        orderItemId: uniqueId + i,
                        isActive: true,
                      }))
                    : [],
            }),
          );
          updated[index].reelDetail = [...existingRecap, ...additional];
        } else if (quantity < existingRecap.length) {
          // Smart removal: always keep delivered reels, remove non-delivered first.
          // isReelDelivered lives on the backend reel (editEvents.reels), not on the
          // in-memory reelDetail item, so look it up by matching all possible id fields.
          const backendReels = props.editEvents?.reels ?? [];
          const checkDelivered = (r) => {
            const match = backendReels.find(
              (br) =>
                br.id === r.id ||
                br.reelDetail?.id === r.id ||
                br.reelDetail?.orderItemId === r.id,
            );
            return !!match?.isReelDelivered;
          };
          const deliveredReels = existingRecap.filter(checkDelivered);
          const nonDeliveredReels = existingRecap.filter(
            (r) => !checkDelivered(r),
          );
          const keepNonDelivered = Math.max(
            0,
            quantity - deliveredReels.length,
          );
          updated[index].reelDetail = [
            ...deliveredReels,
            ...nonDeliveredReels.slice(0, keepNonDelivered),
          ];
        }
      } else {
        updated[index].reelDetail = [];
      }

      updated[index].quantity = quantity;
    }
    setAddRecapz(updated);
  };

  const handleEventCheckboxChange = (eventId, isChecked, eventName) => {
    const updated = [...addRecapz];

    if (isChecked) {
      // Add new entity
      updated.push({
        eventId,
        isChecked,
        eventName,
        isCombinedEventsReel: eventId === ALL_EVENT.id,
      });
    } else {
      const index = updated.findIndex((x) => x.eventId === eventId);
      if (index !== -1) updated.splice(index, 1);
    }

    // Reorder reacap list (combined event always at the end)
    const reordered = getReOrderedRecapList(updated, formState);

    setAddRecapz(reordered);
  };

  const allEventsReel = addRecapz.find((e) => e.eventId === ALL_EVENT.id);

  const handleNextClick = () => {
    //compare existing recaps with updated recaps and show confirmation model if anything has changed
    if (props.isEditEvent || isAddUpdateRecap) {
      const isIdenticalRecapz = isAddRecapzIdentical(
        addRecapz,
        addRecapzBackup,
      );

      if (!isIdenticalRecapz) {
        setShowModal(true);
        setRecapBriefConfirmModalMessage(
          `${messages.REGENERATE_EDIT_BRIEF_CONFIRMATION_MESSAGE}`,
        );
        return;
      }
    }
    confirmHandleNextClick();
  };

  const confirmHandleNextClick = () => {
    // Filter valid items
    const validItems = addRecapz.filter((item) => item.isChecked === true);

    if (validItems.length === 0) {
      customToast(t("please_select_quantity"), toast.TYPE.ERROR);
      return;
    }
    if (validItems.some((item) => !item.quantity || item.quantity <= 0)) {
      customToast(t("quantity_is_not_selected"), toast.TYPE.ERROR);
      return;
    }

    // Check combined reel have at least one events is selected
    const combinedReel = validItems.find((item) => item.isCombinedEventsReel);
    if (combinedReel) {
      const hasEventsSelected = combinedReel.reelDetail?.some(
        (rd) =>
          isArrayWithItems(rd.eventOrderItemMap) &&
          rd.eventOrderItemMap.filter((item) => item.isActive ?? true).length >=
            2,
      );

      if (!hasEventsSelected) {
        customToast(
          messages.PLEASE_SELECT_EVENTS_FOR_COMBINED_REEL,
          toast.TYPE.ERROR,
        );
        return;
      }
    }

    setAddRecapz(validItems);
    // Proceed to next step
    handleActiveStep(ADD_RECAPZ_STEPS.SELECT_RECAP_TYPE);
  };

  const handleConfirm = () => {
    props.setRegenerateEditBriefs(true);
    confirmHandleNextClick();
    setRecapBriefConfirmModalMessage(EMPTY_STRING);
    setShowModal(false);
  };

  // options of combined events selection dropdown from formState(events)
  const eventOptions = formState?.map((event) => ({
    value: event.id,
    label: event.eventName,
  }));

  // get selected events from addRecapz in case of combined event reel
  const selectedEventIds =
    addRecapz
      ?.find((recap) => recap.isCombinedEventsReel)
      ?.reelDetail?.[0]?.eventOrderItemMap?.filter(
        (e) => e.isActive === undefined || e.isActive === true,
      )
      ?.map((e) => e.eventId) || [];

  // handle selection change of combined events dropdown
  const handleEventsSelect = (selectedOptions = []) => {
    const updatedAddRecapz = addRecapz.map((recap) => {
      if (!recap.isCombinedEventsReel || !isArrayWithItems(recap?.reelDetail))
        return recap;

      const updatedReelDetail = recap.reelDetail.map((detail) => {
        const existingMap = detail.eventOrderItemMap ?? [];

        //Build updated map — preserving existing data or restoring from combinedRecapsEventMap
        const updatedMap = selectedOptions.map((eventId) => {
          // Try to find existing mapping first
          const existing = existingMap.find((x) => x.eventId === eventId);

          return {
            ...(existing || {}),
            eventId,
            orderItemId: detail.id,
            isActive: true,
          };
        });

        // Merge active + inactive mappings
        const finalMap = [...updatedMap];

        return {
          ...detail,
          eventOrderItemMap: finalMap,
        };
      });

      return { ...recap, reelDetail: updatedReelDetail };
    });

    props.setAddRecapz(updatedAddRecapz);
  };

  const addRecapzBackup = useMemo(() => {
    const reels = props?.editEvents?.reels ?? [];
    return transformReelsInEditEvent(reels, ALL_EVENT.id);
  }, [props.editEvents]);

  // Delivered reel info per event (memoised)
  const deliveredInfoMap = useMemo(() => {
    const map = {};
    if (formState) {
      formState.forEach((event) => {
        map[event.id] = getDeliveredReelInfo(event.id, props.editEvents);
      });
    }
    map[ALL_EVENT.id] = getDeliveredReelInfo(ALL_EVENT.id, props.editEvents);
    return map;
  }, [formState, props.editEvents]);

  const {
    hasDelivered: combinedHasDelivered,
    deliveredCount: combinedDeliveredCount,
  } = deliveredInfoMap[ALL_EVENT.id] ?? {};

  return (
    <div className="select-event-quantity-container col-12 col-md-8 col-lg-6 margin-0-auto">
      <p className="add-recapz-heading label-content-600 mb-2">
        which bookings do you want to add edits for?
      </p>

      <AvForm id="add-recapz" autoComplete="off">
        <div id="form-content" className="px-md-5 pt-4 text-left">
          {formState?.map((event) => {
            const selectedEvent = props.addRecapz.find(
              (e) => e.eventId === event.id,
            );
            const { hasDelivered, deliveredCount } =
              deliveredInfoMap[event.id] ?? {};

            return (
              <div
                key={event.id}
                className="d-md-flex justify-content-between d-sm-block mt-2"
              >
                <div
                  className={`d-flex align-items-center quanity-checkbox-container`}
                >
                  <input
                    type="checkbox"
                    checked={selectedEvent?.isChecked || false}
                    disabled={hasDelivered}
                    onChange={(e) =>
                      handleEventCheckboxChange(
                        event.id,
                        e.target.checked,
                        event.eventName,
                      )
                    }
                  />

                  <span className="event-name pl-2">{event.eventName}</span>
                </div>
                <div className="d-flex align-items-center pl-1">
                  {selectedEvent?.isChecked && (
                    <>
                      <span className="quantity pr-2">quantity:</span>
                      <InputGroupWrapper>
                        <AvField
                          type="select"
                          name={"quantity" + event?.id}
                          className={
                            "pr-5 pr-md-2 select-dropdown recap-quantity-dropdown"
                          }
                          value={selectedEvent?.quantity}
                          onChange={(e) =>
                            handleQuantityChange(event.id, e.target.value)
                          }
                        >
                          <option selected disabled value="">
                            select
                          </option>
                          {window.config?.eventRecapQuantityOption?.map(
                            (option) => (
                              <option
                                key={option.id}
                                value={option.value}
                                disabled={
                                  hasDelivered &&
                                  parseInt(option.value, 10) < deliveredCount
                                }
                              >
                                {option.label}
                              </option>
                            ),
                          )}
                        </AvField>
                        <ChevronDownIcon />
                      </InputGroupWrapper>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {formState?.length !== 1 && (
            <div className="d-md-flex justify-content-between d-sm-block mt-2">
              <div
                className={
                  "d-flex align-items-center quanity-checkbox-container"
                }
              >
                <input
                  type="checkbox"
                  name="allEventCheckbox"
                  checked={allEventsReel?.isChecked || false}
                  disabled={combinedHasDelivered}
                  onChange={(e) =>
                    handleEventCheckboxChange(
                      ALL_EVENT.id,
                      e.target.checked,
                      ALL_EVENT.name,
                    )
                  }
                />
                <span className="event-name pl-2">{ALL_EVENT.name}</span>
                <div className="pl-1">
                  <CustomTooltip
                    id="tooltip"
                    title="a combined edit lets you create one or more recap videos using footage from multiple events."
                    arrow
                    placement="right"
                  >
                    <span tabIndex="0">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="info-icon"
                      />
                    </span>
                  </CustomTooltip>
                </div>
              </div>
              <div className="d-flex align-items-center">
                {allEventsReel?.isChecked && (
                  <>
                    <span className="quantity pr-2">quantity:</span>
                    <InputGroupWrapper>
                      <AvField
                        type="select"
                        name={"quantity" + ALL_EVENT.id}
                        className={
                          "pr-5 pr-md-2 select-dropdown recap-quantity-dropdown"
                        }
                        value={allEventsReel?.quantity}
                        onChange={(e) =>
                          handleQuantityChange(ALL_EVENT.id, e.target.value)
                        }
                      >
                        <option selected disabled value="">
                          select
                        </option>
                        {window.config?.eventRecapQuantityOption?.map(
                          (option) => (
                            <option
                              key={option.id}
                              value={option.value}
                              disabled={
                                combinedHasDelivered &&
                                parseInt(option.value, 10) <
                                  combinedDeliveredCount
                              }
                            >
                              {option.label}
                            </option>
                          ),
                        )}
                      </AvField>
                      <ChevronDownIcon />
                    </InputGroupWrapper>
                  </>
                )}
              </div>
            </div>
          )}

          {allEventsReel?.isChecked &&
            isArrayWithItems(allEventsReel?.reelDetail) &&
            formState?.length > 1 && (
              <div className="mt-2">
                <MultiSelectDropdown
                  options={eventOptions}
                  selectedValues={selectedEventIds}
                  onSelectionChange={handleEventsSelect}
                  label={`${messages.SELECT_EVENTS}:`}
                  valueKey="value"
                  placeholder="Select events..."
                  disabled={combinedHasDelivered}
                />
              </div>
            )}
        </div>
      </AvForm>

      <div className="d-flex justify-content-center sticky-button-row mt-4">
        {!isAddUpdateRecap && (
          <button
            type="button"
            className="event-back-btn button-common button-outline btn-text-grey width-50"
            onClick={() => handleActiveStep(ADD_RECAPZ_STEPS.ADD_BOOKING)}
          >
            <BackIcon />
            <span className="pl-1">{t("go_back")}</span>
          </button>
        )}
        <button
          type="button"
          className="button-common button-fill width-50 ml-3"
          onClick={handleNextClick}
        >
          {t("next")}
        </button>
      </div>

      <ConfirmationModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirm}
        dialogText={`${recapBriefConfirmModalMessage}`}
        maxWidth="xs"
      />
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  addRecapz: state.eventBooking.addRecapz,
  recapTypes: state.eventBooking.recapTypes,
  isEditEvent: state.eventBooking.isEditEvent,
  editEvents: state.eventBooking.editEvents,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setAddRecapz: (data) => dispatch(eventBookingActions.setAddRecapz(data)),
  setRegenerateEditBriefs: (data) =>
    dispatch(eventBookingActions.setRegenerateEditBriefs(data)),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SelectEventQuantity);
